/**
 * Small helper components used by the AdminProductEdit page.
 * Extracted from AdminProductEdit.jsx to keep the main page under 1000 lines.
 * These are pure presentational + one small file-upload widget — no product state
 * lives here.
 */
import React, { useState } from "react";
import {
  Sparkles, Plus, X, Loader2, CheckCircle2, XCircle, RotateCw, Undo2,
} from "lucide-react";
import { api, API, fileUrl } from "@/lib/api";

export function emptySuggestions() {
  return {
    recommended_applications: [],
    maintenance_instructions: [],
    faqs: [],
    use_cases: [],
    installation_recommendations: [],
  };
}

export function Section({ title, action, children }) {
  return (
    <div className="hed-card p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="hed-overline flex-1">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="hed-overline block mb-1" style={{ fontSize: 10 }}>{label}</label>
      {children}
    </div>
  );
}

export function ListEditor({ items, onChange, onAdd, onRemove, testId, placeholder }) {
  return (
    <div className="space-y-2">
      {(items || []).map((v, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            data-testid={`${testId}-input-${idx}`}
            value={v}
            placeholder={placeholder}
            onChange={(e) => onChange(idx, e.target.value)}
            className="flex-1 bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-1.5 text-sm"
          />
          <button
            onClick={() => onRemove(idx)}
            className="text-[#5C5C54] hover:text-[#9E2A2B]"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        onClick={onAdd}
        className="text-xs text-[#5E6033] hover:text-[#B5953B] inline-flex items-center gap-1 mt-2"
        data-testid={`${testId}-add-button`}
      >
        <Plus size={12} strokeWidth={1.5} /> Add item
      </button>
    </div>
  );
}

export function ReextractButton({ field, label, busy, onClick, canUndo, onUndo }) {
  return (
    <div className="flex items-center gap-1.5">
      {canUndo && (
        <button
          type="button"
          onClick={onUndo}
          disabled={!!busy}
          title={`Undo last AI re-extraction of "${label}"`}
          data-testid={`undo-${field}`}
          className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E8E6DF] hover:border-[#9E2A2B] hover:bg-[#FAF3F3] text-[#5C5C54] hover:text-[#9E2A2B] disabled:opacity-50 transition-colors"
        >
          <Undo2 size={10} strokeWidth={2} />
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={!!busy}
        title={`Re-extract "${label}" from the original PDF using AI`}
        data-testid={`reextract-${field}`}
        className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#E8E6DF] hover:border-[#B5953B] hover:bg-[#FAF7EC] text-[#5C5C54] hover:text-[#5E6033] disabled:opacity-50 transition-colors"
      >
        {busy ? (
          <Loader2 size={10} className="animate-spin" strokeWidth={2} />
        ) : (
          <RotateCw size={10} strokeWidth={2} />
        )}
        AI Re-extract
      </button>
    </div>
  );
}

export function SuggestionGroup({ title, items, isApproved, toggle, testIdPrefix }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-[#1A1A18] mb-2">{title}</div>
      <div className="space-y-1.5">
        {items.map((v, idx) => {
          const approved = isApproved(v);
          return (
            <div
              key={idx}
              className="flex items-start gap-2 text-sm bg-white border border-[#E8E6DF] rounded-sm p-2.5"
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <button
                onClick={() => toggle(v)}
                className={`flex-shrink-0 mt-0.5 p-0.5 rounded-sm transition-colors ${
                  approved ? "text-[#3A5A40]" : "text-[#5C5C54] hover:text-[#3A5A40]"
                }`}
                data-testid={`${testIdPrefix}-toggle-${idx}`}
              >
                {approved ? (
                  <CheckCircle2 size={16} strokeWidth={1.5} />
                ) : (
                  <XCircle size={16} strokeWidth={1.5} />
                )}
              </button>
              <div className="flex-1">{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SuggestionFaqGroup({ items, isApproved, toggle }) {
  if (!items?.length) return null;
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-[#1A1A18] mb-2">FAQs</div>
      <div className="space-y-1.5">
        {items.map((f, idx) => {
          const approved = isApproved(f);
          return (
            <div
              key={idx}
              className="flex items-start gap-2 text-sm bg-white border border-[#E8E6DF] rounded-sm p-2.5"
              data-testid={`sugg-faq-item-${idx}`}
            >
              <button
                onClick={() => toggle(f)}
                className={`flex-shrink-0 mt-0.5 ${
                  approved ? "text-[#3A5A40]" : "text-[#5C5C54] hover:text-[#3A5A40]"
                }`}
                data-testid={`sugg-faq-toggle-${idx}`}
              >
                {approved ? (
                  <CheckCircle2 size={16} strokeWidth={1.5} />
                ) : (
                  <XCircle size={16} strokeWidth={1.5} />
                )}
              </button>
              <div className="flex-1">
                <div className="font-medium">{f.question}</div>
                <div className="text-[#5C5C54] text-xs mt-1">{f.answer}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Small widget for attaching additional PDFs/images to an existing product
 * without breaking the product's QR code (attach → optional re-extract).
 */
export function SourceFilesEditor({ productId, files, onChange }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    try {
      const uploadedIds = { documents: [], images: [] };
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const isImage = (f.type || "").startsWith("image/");
        fd.append("kind", isImage ? "image" : "document");
        const token = localStorage.getItem("heda_token");
        const res = await fetch(`${API}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (isImage) uploadedIds.images.push(data.id);
        else uploadedIds.documents.push(data.id);
      }
      const { data } = await api.post(`/products/${productId}/attach-files`, {
        file_ids: uploadedIds.documents,
        image_ids: uploadedIds.images,
      });
      onChange({ files: data.files || [], images: data.images || [] });
      if (
        window.confirm(
          "Files attached. Run AI extraction now so the new content is merged into this product?\n\n(Other fields are kept — AI re-extracts everything across the combined file set.)",
        )
      ) {
        try {
          await api.post(`/products/${productId}/extract`);
        } catch (e) {
          alert(e?.response?.data?.detail || "Could not start extraction");
        }
      }
    } catch (e) {
      alert(`Upload failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async (fileId) => {
    if (!window.confirm("Remove this PDF from the product?\n\nThe file stays in your storage but is no longer used for AI extraction.")) return;
    try {
      await api.delete(`/products/${productId}/files/${fileId}`);
      const { data } = await api.get(`/products/${productId}`);
      onChange({ files: data.files || [], images: data.images || [] });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not detach file");
    }
  };

  return (
    <div className="space-y-3">
      {(files || []).length > 0 ? (
        <div className="space-y-2">
          {files.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-3 text-sm px-3 py-2 bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm"
              data-testid={`source-file-${f.id}`}
            >
              <a
                href={fileUrl(f.storage_path)}
                target="_blank"
                rel="noreferrer"
                className="truncate flex items-center gap-2 flex-1 min-w-0 text-[#1A1A18] hover:text-[#B5953B] transition-colors"
              >
                <Sparkles size={12} strokeWidth={1.5} className="text-[#5E6033] flex-shrink-0" />
                <span className="truncate">{f.original_filename}</span>
                <span className="text-xs text-[#5C5C54] flex-shrink-0">
                  {Math.round((f.size || 0) / 1024)} KB
                </span>
              </a>
              <button
                type="button"
                onClick={() => handleDetach(f.id)}
                disabled={busy}
                className="text-[#5C5C54] hover:text-[#9E2A2B] flex-shrink-0 disabled:opacity-40"
                data-testid={`detach-file-${f.id}`}
                title="Remove this PDF from the product"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[#5C5C54] italic">
          No PDFs attached yet. Drop one below — the AI will read it.
        </div>
      )}

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleUpload(Array.from(e.dataTransfer.files));
        }}
        className={`block border border-dashed cursor-pointer text-center py-4 px-3 rounded-sm transition-colors ${
          drag ? "border-[#B5953B] bg-[#F2F0EA]" : "border-[#E8E6DF] hover:bg-[#FAFAF8]"
        } ${busy ? "opacity-50 cursor-wait" : ""}`}
        data-testid="attach-pdf-dropzone"
      >
        <input
          type="file"
          accept=".pdf,application/pdf,image/*"
          multiple
          disabled={busy}
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          data-testid="attach-pdf-input"
        />
        {busy ? (
          <div className="text-xs text-[#5C5C54] inline-flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Attaching…
          </div>
        ) : (
          <div className="text-xs">
            <span className="font-medium text-[#1A1A18]">+ Add more PDFs</span>{" "}
            <span className="text-[#5C5C54]">— drop or click to attach. QR stays the same.</span>
          </div>
        )}
      </label>
    </div>
  );
}
